import { jsPDF } from 'jspdf'

export const loadLogoForPdf = (): Promise<{ data: string; width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.drawImage(img, 0, 0)
        resolve({ data: canvas.toDataURL('image/png'), width: img.width, height: img.height })
      } else {
        reject(new Error('Could not get canvas context'))
      }
    }
    img.onerror = reject
    img.src = '/logo.png'
  })
}

// Color palette (RGB tuples)
export const C = {
  navy:      [15,  23,  42]  as [number, number, number],
  navyMid:   [30,  41,  59]  as [number, number, number],
  blue:      [59,  82,  208] as [number, number, number],
  blueLight: [99,  131, 242] as [number, number, number],
  pink:      [236, 72,  153] as [number, number, number],
  pinkLight: [249, 168, 212] as [number, number, number],
  white:     [255, 255, 255] as [number, number, number],
  offWhite:  [248, 250, 252] as [number, number, number],
  slate200:  [226, 232, 240] as [number, number, number],
  slate500:  [100, 116, 139] as [number, number, number],
  slate700:  [51,  65,  85]  as [number, number, number],
  slate900:  [15,  23,  42]  as [number, number, number],
  emerald:   [16,  185, 129] as [number, number, number],
  amber:     [245, 158, 11]  as [number, number, number],
  rose:      [244, 63,  94]  as [number, number, number],
}

export const setRgb = (doc: jsPDF, rgb: [number, number, number]) =>
  doc.setTextColor(rgb[0], rgb[1], rgb[2])
export const fillRgb = (doc: jsPDF, rgb: [number, number, number]) =>
  doc.setFillColor(rgb[0], rgb[1], rgb[2])
export const drawRgb = (doc: jsPDF, rgb: [number, number, number]) =>
  doc.setDrawColor(rgb[0], rgb[1], rgb[2])

/** Wrap a string to fit within maxWidth at the current font size */
export const wrapText = (doc: jsPDF, text: string, maxWidth: number): string[] =>
  doc.splitTextToSize(text, maxWidth)

/** Page geometry */
export const PW = 210  // A4 width mm
export const PH = 297  // A4 height mm
export const ML = 18   // margin left
export const MR = 18   // margin right
export const MT = 14   // margin top (content pages)
export const MB = 16   // margin bottom
export const CW = PW - ML - MR  // content width

// ─────────────────────────────────────────────────────────────────────────────
// PDF running header + footer
// ─────────────────────────────────────────────────────────────────────────────

export function addPageChrome(
  doc: jsPDF,
  companyName: string,
  date: string,
  pageNum: number,
  totalPages: number,
  logoData: { data: string; width: number; height: number } | null,
  documentType: string = 'INVESTMENT MEMORANDUM'
) {
  // ── Header ──────────────────────────────────────
  fillRgb(doc, C.navy)
  doc.rect(0, 0, PW, 10, 'F')

  doc.setFontSize(7)
  doc.setFont('helvetica', 'bold')
  setRgb(doc, [180, 190, 210])
  doc.text(documentType.toUpperCase(), ML, 6.5)
  setRgb(doc, C.white)
  doc.text(`${companyName.toUpperCase()}  ·  ${date}`, PW - MR, 6.5, { align: 'right' })

  // ── Footer ──────────────────────────────────────
  const footerY = PH - 8
  // thin gradient-like line: blue then pink
  fillRgb(doc, C.blue)
  doc.rect(0, footerY - 1, PW * 0.6, 0.5, 'F')
  fillRgb(doc, C.pink)
  doc.rect(PW * 0.6, footerY - 1, PW * 0.4, 0.5, 'F')

  doc.setFontSize(6.5)
  doc.setFont('helvetica', 'normal')
  setRgb(doc, C.slate500)

  // Logo + brand on the left
  if (logoData) {
    const lh = 4.5
    const lw = (logoData.width / logoData.height) * lh
    doc.addImage(logoData.data, 'PNG', ML, footerY + 0.5, lw, lh)
    doc.setFont('helvetica', 'bold')
    setRgb(doc, C.navyMid)
    doc.text('uniscout.ai', ML + lw + 1.5, footerY + 3.8)
  }

  doc.setFont('helvetica', 'normal')
  setRgb(doc, C.slate500)
  doc.text('CONFIDENTIAL', PW / 2, footerY + 3.5, { align: 'center' })
  doc.text(`${pageNum} / ${totalPages}`, PW - MR, footerY + 3.5, { align: 'right' })
}

// ─────────────────────────────────────────────────────────────────────────────
// Low-level PDF rendering primitives
// ─────────────────────────────────────────────────────────────────────────────

export interface RenderCtx {
  doc: jsPDF
  y: number
  companyName: string
  date: string
  pageNum: number
  totalPages: number
  logoData: { data: string; width: number; height: number } | null
  documentType?: string
}

/** Ensure there's at least `needed` mm remaining; add a page if not */
export function ensureSpace(ctx: RenderCtx, needed: number) {
  if (ctx.y + needed > PH - MB - 10) {
    ctx.doc.addPage()
    ctx.pageNum++
    addPageChrome(ctx.doc, ctx.companyName, ctx.date, ctx.pageNum, ctx.totalPages, ctx.logoData, ctx.documentType)
    ctx.y = MT + 12
  }
}

/** Section header band */
export function renderH2(ctx: RenderCtx, title: string) {
  ensureSpace(ctx, 14)
  const bh = 9.5
  fillRgb(ctx.doc, C.navy)
  ctx.doc.rect(0, ctx.y, PW, bh, 'F')

  // Dual-color left accent bar
  fillRgb(ctx.doc, C.blue)
  ctx.doc.rect(0, ctx.y, 3, bh / 2, 'F')
  fillRgb(ctx.doc, C.pink)
  ctx.doc.rect(0, ctx.y + bh / 2, 3, bh / 2, 'F')

  ctx.doc.setFontSize(10.5)
  ctx.doc.setFont('helvetica', 'bold')
  setRgb(ctx.doc, C.white)
  ctx.doc.text(title.toUpperCase(), ML + 4, ctx.y + 6.3)
  ctx.y += bh + 5
}

/** Sub-section heading */
export function renderH3(ctx: RenderCtx, title: string) {
  ensureSpace(ctx, 10)
  ctx.doc.setFontSize(9)
  ctx.doc.setFont('helvetica', 'bold')
  // Small circle dot accent
  fillRgb(ctx.doc, C.blue)
  ctx.doc.circle(ML + 1.5, ctx.y - 0.5, 1.2, 'F')
  setRgb(ctx.doc, C.blue)
  ctx.doc.text(title, ML + 5, ctx.y + 0.5)
  // Underline
  drawRgb(ctx.doc, C.slate200)
  ctx.doc.setLineWidth(0.2)
  ctx.doc.line(ML + 5, ctx.y + 2, ML + 5 + ctx.doc.getTextWidth(title), ctx.y + 2)
  ctx.y += 6
}

/** Prose paragraph — plain text, no markdown */
export function renderParagraph(ctx: RenderCtx, text: string, indent = 0) {
  if (!text?.trim()) return
  const x = ML + indent
  const width = CW - indent
  ctx.doc.setFontSize(9.5)
  ctx.doc.setFont('helvetica', 'normal')
  setRgb(ctx.doc, C.slate700)
  const lines = wrapText(ctx.doc, text.trim(), width)
  for (const line of lines) {
    ensureSpace(ctx, 5.5)
    ctx.doc.text(line, x, ctx.y)
    ctx.y += 5.2
  }
  ctx.y += 2.5
}

/** Em-dash bullet item */
export function renderBullet(ctx: RenderCtx, text: string) {
  ensureSpace(ctx, 5.5)
  ctx.doc.setFontSize(9.5)
  ctx.doc.setFont('helvetica', 'normal')
  setRgb(ctx.doc, C.blue)
  ctx.doc.text('—', ML + 2, ctx.y)
  setRgb(ctx.doc, C.slate700)
  const lines = wrapText(ctx.doc, text.trim(), CW - 10)
  ctx.doc.text(lines[0], ML + 7, ctx.y)
  ctx.y += 5.2
  for (let i = 1; i < lines.length; i++) {
    ensureSpace(ctx, 5.5)
    ctx.doc.text(lines[i], ML + 7, ctx.y)
    ctx.y += 5.2
  }
}

/** Numbered list item */
export function renderNumbered(ctx: RenderCtx, n: number, text: string) {
  ensureSpace(ctx, 5.5)
  ctx.doc.setFontSize(9.5)
  ctx.doc.setFont('helvetica', 'bold')
  setRgb(ctx.doc, C.blue)
  ctx.doc.text(`${n}.`, ML + 2, ctx.y)
  ctx.doc.setFont('helvetica', 'normal')
  setRgb(ctx.doc, C.slate700)
  const lines = wrapText(ctx.doc, text.trim(), CW - 10)
  ctx.doc.text(lines[0], ML + 7, ctx.y)
  ctx.y += 5.2
  for (let i = 1; i < lines.length; i++) {
    ensureSpace(ctx, 5.5)
    ctx.doc.text(lines[i], ML + 7, ctx.y)
    ctx.y += 5.2
  }
}

/** Two-column KPI table */
export function renderKpiTable(ctx: RenderCtx, kpis: { label: string; value: string }[]) {
  if (!kpis.length) return
  const colW = CW / 2 - 2
  const cellH = 9
  let col = 0
  let rowY = ctx.y

  for (let i = 0; i < kpis.length; i++) {
    const x = ML + col * (colW + 4)
    ensureSpace(ctx, cellH + 2)

    // Cell background
    fillRgb(ctx.doc, col === 0 ? [237, 242, 255] : [245, 247, 255])
    ctx.doc.roundedRect(x, rowY - 1, colW, cellH, 1, 1, 'F')

    // Label
    ctx.doc.setFontSize(7)
    ctx.doc.setFont('helvetica', 'bold')
    setRgb(ctx.doc, C.slate500)
    ctx.doc.text(kpis[i].label.toUpperCase(), x + 3, rowY + 3)

    // Value
    ctx.doc.setFontSize(9.5)
    ctx.doc.setFont('helvetica', 'bold')
    setRgb(ctx.doc, C.navy)
    ctx.doc.text(kpis[i].value, x + 3, rowY + 7.5)

    col++
    if (col === 2) {
      col = 0
      rowY += cellH + 3
    }
  }
  // If odd number, advance past incomplete row
  if (col !== 0) rowY += cellH + 3
  ctx.y = rowY + 2
}

/** TAM/SAM/SOM three-column bar */
export function renderMarketFigures(ctx: RenderCtx, tam: string, sam: string, som: string, cagr?: string | null) {
  ensureSpace(ctx, 20)
  const items = [
    { label: 'TAM', value: tam, color: C.blue },
    { label: 'SAM', value: sam, color: C.blueLight },
    { label: 'SOM', value: som, color: C.pink },
  ]
  const boxW = (CW - 8) / 3
  const boxH = 16
  for (let i = 0; i < items.length; i++) {
    const x = ML + i * (boxW + 4)
    fillRgb(ctx.doc, items[i].color)
    ctx.doc.roundedRect(x, ctx.y, boxW, boxH, 2, 2, 'F')

    ctx.doc.setFontSize(7)
    ctx.doc.setFont('helvetica', 'bold')
    setRgb(ctx.doc, [200, 210, 255])
    ctx.doc.text(items[i].label, x + 3, ctx.y + 5)

    ctx.doc.setFontSize(9)
    ctx.doc.setFont('helvetica', 'bold')
    setRgb(ctx.doc, C.white)
    const lines = wrapText(ctx.doc, items[i].value, boxW - 6)
    ctx.doc.text(lines[0], x + 3, ctx.y + 11)
  }
  ctx.y += boxH + 3

  if (cagr) {
    ctx.doc.setFontSize(8)
    ctx.doc.setFont('helvetica', 'bold')
    setRgb(ctx.doc, C.slate500)
    ctx.doc.text(`CAGR: ${cagr}`, ML, ctx.y)
    ctx.y += 6
  }
  ctx.y += 2
}

/** Risk severity badge */
const SEV_COLORS: Record<string, [number, number, number]> = {
  low:    [16, 185, 129],
  medium: [245, 158, 11],
  high:   [244, 63, 94],
}

export function renderRiskRow(ctx: RenderCtx, name: string, desc: string, severity: string) {
  ensureSpace(ctx, 14)
  const sev = severity?.toLowerCase() ?? 'medium'
  const sevColor = SEV_COLORS[sev] ?? C.amber

  // Severity bar (left edge)
  fillRgb(ctx.doc, sevColor)
  ctx.doc.rect(ML, ctx.y - 1, 2.5, 10, 'F')

  // Name
  ctx.doc.setFontSize(8.5)
  ctx.doc.setFont('helvetica', 'bold')
  setRgb(ctx.doc, C.navy)
  ctx.doc.text(name, ML + 5.5, ctx.y + 3.5)

  // Severity badge
  const badgeX = ML + 5.5 + ctx.doc.getTextWidth(name) + 2
  fillRgb(ctx.doc, sevColor)
  ctx.doc.roundedRect(badgeX, ctx.y + 0.5, 18, 5, 1.5, 1.5, 'F')
  ctx.doc.setFontSize(6)
  ctx.doc.setFont('helvetica', 'bold')
  setRgb(ctx.doc, C.white)
  ctx.doc.text(sev.toUpperCase(), badgeX + 9, ctx.y + 4.2, { align: 'center' })

  // Description
  ctx.doc.setFontSize(8.5)
  ctx.doc.setFont('helvetica', 'normal')
  setRgb(ctx.doc, C.slate700)
  const lines = wrapText(ctx.doc, desc, CW - 10)
  ctx.doc.text(lines[0], ML + 5.5, ctx.y + 8)
  ctx.y += 11

  if (lines.length > 1) {
    for (let i = 1; i < lines.length; i++) {
      ensureSpace(ctx, 5.5)
      ctx.doc.text(lines[i], ML + 5.5, ctx.y)
      ctx.y += 5.2
    }
  }
  ctx.y += 2
}

/** Recommendation verdict block */
export function renderVerdictBlock(ctx: RenderCtx, verdict: string, rationale: string) {
  ensureSpace(ctx, 32)
  const verdictColor =
    verdict === 'Strong Invest' ? C.emerald :
    verdict === 'Invest'        ? C.emerald :
    verdict === 'Hold'          ? C.amber :
    C.rose

  // Outer box
  fillRgb(ctx.doc, C.offWhite)
  drawRgb(ctx.doc, verdictColor)
  ctx.doc.setLineWidth(0.4)
  ctx.doc.roundedRect(ML, ctx.y, CW, 28, 2, 2, 'FD')

  // Left colored band
  fillRgb(ctx.doc, verdictColor)
  ctx.doc.roundedRect(ML, ctx.y, 6, 28, 2, 2, 'F')
  ctx.doc.rect(ML + 3, ctx.y, 3, 28, 'F') // square off right edge of band

  // Verdict label
  ctx.doc.setFontSize(8)
  ctx.doc.setFont('helvetica', 'bold')
  setRgb(ctx.doc, verdictColor)
  ctx.doc.text('RECOMMENDATION', ML + 10, ctx.y + 7)

  // Verdict text
  ctx.doc.setFontSize(16)
  ctx.doc.setFont('helvetica', 'bold')
  setRgb(ctx.doc, C.navy)
  ctx.doc.text(verdict, ML + 10, ctx.y + 15)

  // Rationale
  ctx.doc.setFontSize(8.5)
  ctx.doc.setFont('helvetica', 'normal')
  setRgb(ctx.doc, C.slate700)
  const lines = wrapText(ctx.doc, rationale, CW - 16)
  const maxLines = Math.min(lines.length, 2)
  for (let i = 0; i < maxLines; i++) {
    ctx.doc.text(lines[i], ML + 10, ctx.y + 20 + i * 4.5)
  }

  ctx.y += 32
}

/** Inline label-value row (for terms) */
export function renderLabelValue(ctx: RenderCtx, label: string, value: string) {
  ensureSpace(ctx, 6)
  ctx.doc.setFontSize(8.5)
  ctx.doc.setFont('helvetica', 'bold')
  setRgb(ctx.doc, C.slate500)
  ctx.doc.text(`${label}:`, ML + 2, ctx.y)
  ctx.doc.setFont('helvetica', 'normal')
  setRgb(ctx.doc, C.slate900)
  const valLines = wrapText(ctx.doc, value, CW - 40)
  ctx.doc.text(valLines[0], ML + 35, ctx.y)
  ctx.y += 5.5
}

